import type { ProgramFixedContentRow, ProgramProgressCell } from '../types/domain';
import { buildAnnualCellKey } from './annualTimetable';
import { parseMemoFieldStorageDeep, fixedLearningPlainPrefixEventName } from './programCellDisplayText';

function isFixedProgramLeftoverLearningText(raw: string | null | undefined): boolean {
  return Boolean(fixedLearningPlainPrefixEventName(raw));
}

function fixedLearningHasBodyBeyondPrefix(raw: string | null | undefined): boolean {
  const text = trimText(raw);
  if (!text) return false;
  const inner = text.startsWith('{') ? parseMemoFieldStorageDeep(text).text.trim() : text;
  const ev = fixedLearningPlainPrefixEventName(inner);
  if (!ev) return inner.length > 0;
  const escaped = ev.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const stripped = inner.replace(new RegExp(`^◆\\s*${escaped}\\s*-`), '').trim();
  return stripped.length > 0;
}

function trimText(raw: string | null | undefined): string {
  return (raw ?? '').trim();
}

/** 단원 시드(`◆행사명`) 또는 학습 `◆행사명-` 접두 — 고정 프로그램이 남긴 칸 값 */
export function progressCellHasFixedProgramLeftoverFields(cell: {
  unit_text?: string | null;
  learning_text?: string | null;
}): boolean {
  const unit = trimText(cell.unit_text);
  if (unit.startsWith('◆')) return true;
  return isFixedProgramLeftoverLearningText(cell.learning_text);
}

/** 색을 지운 뒤에도 남겨야 하는 수업·평가 기록만 본다 */
export function progressCellHasRecordsToKeepAfterUnfixedCleanup(cell: {
  record_class_text?: string | null;
  record_memo_text?: string | null;
  record_evaluation_text?: string | null;
  record_cross_curricular_text?: string | null;
  record_member_group_id?: string | null;
}): boolean {
  return Boolean(
    trimText(cell.record_class_text) ||
      trimText(cell.record_memo_text) ||
      trimText(cell.record_evaluation_text) ||
      trimText(cell.record_cross_curricular_text) ||
      trimText(cell.record_member_group_id)
  );
}

function hasExtraFixedContent(row: {
  page_text?: string | null;
  assistant_page_text?: string | null;
  current_period_text?: string | null;
  total_period_text?: string | null;
  material_text?: string | null;
  record_cross_curricular_text?: string | null;
  record_memo_text?: string | null;
  record_evaluation_text?: string | null;
  record_class_text?: string | null;
  learning_note?: string | null;
  presentation_ref_id?: string | null;
  worksheet_ref_id?: string | null;
  record_member_group_id?: string | null;
}): boolean {
  return Boolean(
    trimText(row.page_text) ||
      trimText(row.assistant_page_text) ||
      trimText(row.current_period_text) ||
      trimText(row.total_period_text) ||
      trimText(row.material_text) ||
      trimText(row.record_cross_curricular_text) ||
      trimText(row.record_memo_text) ||
      trimText(row.record_evaluation_text) ||
      trimText(row.record_class_text) ||
      trimText(row.learning_note) ||
      trimText(row.presentation_ref_id) ||
      trimText(row.worksheet_ref_id) ||
      trimText(row.record_member_group_id)
  );
}

/** `◆행사` / `◆행사-` 만 있고 본문·첨부가 없으면 시드 전용 행 */
export function isSeedOnlyFixedContentRow(row: {
  event_name?: string | null;
  unit_text?: string | null;
  learning_text?: string | null;
  page_text?: string | null;
  assistant_page_text?: string | null;
  current_period_text?: string | null;
  total_period_text?: string | null;
  material_text?: string | null;
  record_cross_curricular_text?: string | null;
  record_memo_text?: string | null;
  record_evaluation_text?: string | null;
  record_class_text?: string | null;
  learning_note?: string | null;
  presentation_ref_id?: string | null;
  worksheet_ref_id?: string | null;
  record_member_group_id?: string | null;
}): boolean {
  if (hasExtraFixedContent(row)) return false;
  if (fixedLearningHasBodyBeyondPrefix(row.learning_text)) return false;
  const unit = trimText(row.unit_text);
  const learning = trimText(row.learning_text);
  const unitIsSeed = !unit || /^◆[^◆\n]+$/.test(unit);
  const learningIsSeed = !learning || isFixedProgramLeftoverLearningText(learning);
  return unitIsSeed && learningIsSeed;
}

export type FixedProgramUnfixedSlotCleanupPlan = {
  progressCellIdsToDelete: string[];
  progressCellsToClear: ProgramProgressCell[];
  fixedRowIdsToDelete: string[];
  fixedRowsToUnbind: ProgramFixedContentRow[];
};

/**
 * 더 이상 고정 팔레트 색이 아닌 칸에 묶인 시드·고정행을 지울 대상.
 * `stillFixedAnnualKeys`에 없는 연간 칸만 대상이다.
 * `reassignedAnnualKeys`(행사 색이 아닌 시간표가 들어간 칸)에 묶인 고정행은 내용이 있어도 삭제한다.
 */
export function planFixedProgramUnfixedSlotCleanup(
  stillFixedAnnualKeys: ReadonlySet<string>,
  programCells: ProgramProgressCell[],
  fixedRows: ProgramFixedContentRow[],
  reassignedAnnualKeys?: ReadonlySet<string>
): FixedProgramUnfixedSlotCleanupPlan {
  const progressCellIdsToDelete: string[] = [];
  const progressCellsToClear: ProgramProgressCell[] = [];
  for (const cell of programCells) {
    const annualKey = buildAnnualCellKey(cell.grade_id, cell.monday_iso, cell.weekday_key, cell.slot_index);
    if (stillFixedAnnualKeys.has(annualKey)) continue;
    if (!progressCellHasFixedProgramLeftoverFields(cell)) continue;
    if (!cell.id.trim()) continue;
    if (progressCellHasRecordsToKeepAfterUnfixedCleanup(cell)) {
      progressCellsToClear.push(cell);
    } else {
      progressCellIdsToDelete.push(cell.id);
    }
  }

  const fixedRowIdsToDelete: string[] = [];
  const fixedRowsToUnbind: ProgramFixedContentRow[] = [];
  for (const row of fixedRows) {
    const sourceKey = (row.source_annual_cell_key ?? '').trim();
    if (!sourceKey || stillFixedAnnualKeys.has(sourceKey)) continue;
    if (isSeedOnlyFixedContentRow(row) || reassignedAnnualKeys?.has(sourceKey)) {
      if (row.id.trim()) fixedRowIdsToDelete.push(row.id);
    } else {
      fixedRowsToUnbind.push(row);
    }
  }

  return {
    progressCellIdsToDelete,
    progressCellsToClear,
    fixedRowIdsToDelete,
    fixedRowsToUnbind,
  };
}

export function cleanupPlanHasWork(plan: FixedProgramUnfixedSlotCleanupPlan): boolean {
  return (
    plan.progressCellIdsToDelete.length > 0 ||
    plan.progressCellsToClear.length > 0 ||
    plan.fixedRowIdsToDelete.length > 0 ||
    plan.fixedRowsToUnbind.length > 0
  );
}
